// web/src/scene/loaders/GLBLoader.tsx
import { useEffect, useRef } from 'react'
import type { MutableRefObject } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from 'three-mesh-bvh'

import { useAppSelector } from '../../app/useAppSelector'
import { useAppDispatch } from '../../app/useAppDispatch'
import { mapActions } from '@shared/slices/mapSlice'
import { useSceneRef } from '../SceneRefContext'
import { applyLevelFilter } from './levelFilterUtils'

// Patch Three's raycasting with three-mesh-bvh acceleration (idempotent).
;(THREE.Mesh.prototype as any).raycast = acceleratedRaycast
;(THREE.BufferGeometry.prototype as any).computeBoundsTree = computeBoundsTree
;(THREE.BufferGeometry.prototype as any).disposeBoundsTree = disposeBoundsTree

interface GLBLoaderProps {
  onBoundsReady?: (box: THREE.Box3) => void
}

export function GLBLoader({ onBoundsReady }: GLBLoaderProps) {
  const { scene } = useThree()
  const defaultMapFile = useAppSelector(s => s.map.defaultMapFile)
  const wireframe = useAppSelector(s => s.viewer.showWireframe)
  const filterLevel = useAppSelector(s => s.rtls.filterLevel)
  const dispatch = useAppDispatch()
  const sceneRefHolder = useSceneRef()

  const modelRef = useRef<THREE.Object3D | null>(null)

  // Keep the latest onBoundsReady in a ref so it stays out of the load
  // effect's deps — otherwise a non-stable parent callback would re-fetch
  // the 26 MB GLB on every render.
  const onBoundsReadyRef = useRef(onBoundsReady)
  useEffect(() => {
    onBoundsReadyRef.current = onBoundsReady
  }, [onBoundsReady])

  // --- Load effect -------------------------------------------------------
  useEffect(() => {
    if (!defaultMapFile) return
    const ext = defaultMapFile.split('.').pop()?.toLowerCase()
    if (ext !== 'glb') return

    let cancelled = false
    const url = `/samples/${defaultMapFile}`

    // Dispose any previously-mounted model.
    disposeCurrentModel(scene, modelRef)

    dispatch(mapActions.setLoading({
      fileName: defaultMapFile,
      fileType: 'glb',
      fileSizeBytes: 0,
    }))

    ;(async () => {
      try {
        const res = await fetch(url)
        if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${url}`)
        const buf = await res.arrayBuffer()
        if (cancelled) return

        const loader = new GLTFLoader()
        const draco = new DRACOLoader()
        draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/')
        loader.setDRACOLoader(draco)

        await new Promise<void>((resolve, reject) => {
          loader.parse(
            buf,
            '',
            (gltf) => {
              if (cancelled) { resolve(); return }
              try {
                const model = gltf.scene

                // Compute bounding box early — needed for dispatch and onBoundsReady.
                const box = new THREE.Box3().setFromObject(model)
                const center = new THREE.Vector3()
                box.getCenter(center)

                const tintColor = new THREE.Color(0xffaa23)
                const emissiveColor = new THREE.Color(0x7a5010)
                let vertexCount = 0

                model.traverse((child: THREE.Object3D) => {
                  if (child instanceof THREE.Mesh) {
                    const geo = child.geometry
                    // Vertex count — prefer indexed count to match webview logic
                    if (geo.index) {
                      vertexCount += geo.index.count
                    } else if (geo.attributes.position) {
                      vertexCount += geo.attributes.position.count
                    }

                    // BVH acceleration structure for ground-snap raycasting
                    if ((geo as any).computeBoundsTree) {
                      ;(geo as any).computeBoundsTree()
                    }

                    child.castShadow = true
                    child.receiveShadow = true

                    // Tint map model to copper/orange with emissive for uniform brightness.
                    // Clone materials so the original asset is not mutated.
                    if (Array.isArray(child.material)) {
                      child.material = child.material.map((m: THREE.Material) => {
                        const cloned = m.clone()
                        if ('color' in cloned) {
                          const std = cloned as THREE.MeshStandardMaterial
                          std.color.copy(tintColor)
                          std.emissive.copy(emissiveColor)
                          std.emissiveIntensity = 1.0
                          std.wireframe = wireframe
                        }
                        return cloned
                      })
                    } else if (child.material) {
                      child.material = child.material.clone()
                      if ('color' in child.material) {
                        const std = child.material as THREE.MeshStandardMaterial
                        std.color.copy(tintColor)
                        std.emissive.copy(emissiveColor)
                        std.emissiveIntensity = 1.0
                        std.wireframe = wireframe
                      }
                    }
                  }
                })

                // Tag as map model so raycasting and ground-snap can identify it.
                model.userData.isMapModel = true
                scene.add(model)
                modelRef.current = model

                // Apply level filter immediately after mount (M1 passes null = show all).
                applyLevelFilter(model, null)

                dispatch(mapActions.setModelLoaded({
                  boundingBox: {
                    min: { x: box.min.x, y: box.min.y, z: box.min.z },
                    max: { x: box.max.x, y: box.max.y, z: box.max.z },
                  },
                  vertexCount,
                  center: { x: center.x, y: center.y, z: center.z },
                }))

                onBoundsReadyRef.current?.(box)
                sceneRefHolder.current?.camera.fitToModel()

                draco.dispose()
                resolve()
              } catch (e) { reject(e) }
            },
            (err) => {
              draco.dispose()
              reject(err)
            },
          )
        })
      } catch (err: unknown) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String(err)
        console.error('[GLBLoader]', msg)
        dispatch(mapActions.setLoadError(`GLB load failed: ${msg}`))
      }
    })()

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMapFile, dispatch, scene, sceneRefHolder])

  // --- Wireframe reactive effect -----------------------------------------
  useEffect(() => {
    if (!modelRef.current) return
    modelRef.current.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        mats.forEach((m) => {
          if (m && 'wireframe' in m) (m as any).wireframe = wireframe
        })
      }
    })
  }, [wireframe])

  // --- Level filter reactive effect --------------------------------------
  useEffect(() => {
    // M1 has no zRange computation — pass null (no clipping).
    applyLevelFilter(modelRef.current, null)
  }, [filterLevel])

  return null
}

// --- Helpers -------------------------------------------------------------
function disposeCurrentModel(
  scene: THREE.Scene,
  modelRef: MutableRefObject<THREE.Object3D | null>,
) {
  const current = modelRef.current
  if (!current) return
  current.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      // Dispose BVH tree BEFORE geometry — the tree holds refs into the
      // geometry's buffer; disposing geometry first invalidates them.
      ;(obj.geometry as any)?.disposeBoundsTree?.()
      obj.geometry?.dispose()
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
      mats.forEach((m) => m?.dispose?.())
    }
  })
  scene.remove(current)
  modelRef.current = null
}
